/**
 * Single chokepoint for recording terminal playback events (completed or
 * skipped). Combines:
 *   1. GA4 + first-party funnel event emission (via lib/ga4.ts helpers)
 *   2. localStorage append to tls.recent.v1 (via hooks/useRecentPlays.ts)
 *
 * Called exclusively from contexts/audio-player-context.tsx — the only place
 * that owns the terminal-state lifecycle.
 *
 * Threshold rules (from the For You feed plan):
 *  - completed: played_pct ≥ 0.80
 *  - skipped:   played_pct <  0.20
 *  - in between is "neutral" and intentionally NOT recorded (noise).
 */

import type { Sound } from '@/lib/supabase-types'
import { trackPreviewCompleted, trackPreviewSkipped } from '@/lib/tracking'
import { appendRecentPlay } from '@/hooks/useRecentPlays'

export const COMPLETION_THRESHOLD = 0.8
export const SKIP_THRESHOLD = 0.2

type Source = 'sound_player' | 'sound_card' | 'mobile_player' | 'create_page' | 'voice_generator'

export function recordCompletion(sound: Sound, source: Source = 'sound_player'): void {
  trackPreviewCompleted({
    sound_id: sound.id,
    category: sound.category,
    source,
    played_pct: 1.0,
  })
  appendRecentPlay({
    id: sound.id,
    category: sound.category ?? '',
    vibe_tags: sound.vibe_tags ?? [],
    tags: sound.tags ?? [],
    title: sound.title,
    slug: sound.slug,
    class: 'completed',
    played_pct: 1.0,
    ts: Date.now(),
  })
}

export function recordSkip(
  sound: Sound,
  playedPct: number,
  source: Source = 'sound_player',
): void {
  trackPreviewSkipped({
    sound_id: sound.id,
    category: sound.category,
    source,
    played_pct: playedPct,
  })
  appendRecentPlay({
    id: sound.id,
    category: sound.category ?? '',
    vibe_tags: sound.vibe_tags ?? [],
    tags: sound.tags ?? [],
    title: sound.title,
    slug: sound.slug,
    class: 'skipped',
    played_pct: playedPct,
    ts: Date.now(),
  })
}

/**
 * Classify a played_pct into the right terminal event and dispatch. Returns
 * the event that fired, or null if the value was in the neutral zone.
 */
export function classifyAndRecord(
  sound: Sound,
  playedPct: number,
  source: Source = 'sound_player',
): 'completed' | 'skipped' | null {
  if (playedPct >= COMPLETION_THRESHOLD) {
    recordCompletion(sound, source)
    return 'completed'
  }
  if (playedPct < SKIP_THRESHOLD) {
    recordSkip(sound, playedPct, source)
    return 'skipped'
  }
  return null
}
